import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, KeyRound, Trash2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["client", "driver", "dispatcher", "admin"];
const initials = (fn, ln, email) =>
  ((fn?.[0] || "") + (ln?.[0] || "") || email?.[0] || "?").toUpperCase();

function Modal({ open, title, onClose, children, testid }) {
  if (!open) return null;
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#121214] border border-white/10 p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-xl font-semibold text-white">{title}</h3>
          <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="text-[#8E9AA6] hover:text-white">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inp = "w-full bg-transparent border-b border-white/20 px-0 py-2.5 text-sm text-white focus:border-white focus:outline-none";
const lbl = "block text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-2";
const btn = "px-6 py-3 text-[11px] tracking-[0.25em] uppercase font-medium transition-all";

const rolePill = (r) => {
  const map = { admin: "bg-white text-black", dispatcher: "bg-[#E6E6E6] text-black", driver: "bg-white/15 text-white", client: "bg-white/5 text-[#E6E6E6]" };
  return `inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] ${map[r] || "bg-white/10 text-white"}`;
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (role) params.role = role;
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/users", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [page, pageSize, role, status, q]);

  useEffect(() => { load(); }, [load]);

  const myId = me?.id;

  return (
    <div className="space-y-8" data-testid="admin-users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-2">Operations · Users</div>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-white">User Management</h1>
        </div>
        <button data-testid="admin-users-add-btn" onClick={() => setCreateOpen(true)} className={`bg-white text-black hover:bg-[#E6E6E6] inline-flex items-center gap-2 ${btn}`}>
          <Plus size={14} /> Add User
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <input data-testid="admin-users-search" placeholder="Search name or email…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} className={inp} />
        <select data-testid="admin-users-filter-role" value={role} onChange={(e) => { setPage(1); setRole(e.target.value); }} className={inp + " bg-[#0B0B0C]"}>
          <option value="">All Roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="admin-users-filter-status" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className={inp + " bg-[#0B0B0C]"}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {/* Table */}
      <div className="border border-white/10">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
            <tr>
              <th className="text-left px-4 py-3 font-normal">User</th>
              <th className="text-left px-4 py-3 font-normal">Email</th>
              <th className="text-left px-4 py-3 font-normal">Role</th>
              <th className="text-left px-4 py-3 font-normal">Phone</th>
              <th className="text-left px-4 py-3 font-normal">Status</th>
              <th className="text-left px-4 py-3 font-normal">Created</th>
              <th className="text-right px-4 py-3 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#8E9AA6] text-xs">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#8E9AA6] text-xs">No users.</td></tr>}
            {!loading && items.map((u) => {
              const isMe = u.id === myId;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 inline-flex items-center justify-center bg-white/10 text-[11px] tracking-wide text-white">
                        {initials(u.first_name, u.last_name, u.email)}
                      </span>
                      <span className="text-white">{[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"} {isMe && <span className="text-[#8E9AA6] text-xs">(you)</span>}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#E6E6E6]">{u.email}</td>
                  <td className="px-4 py-3"><span className={rolePill(u.role)}>{u.role}</span></td>
                  <td className="px-4 py-3 text-[#8E9AA6]">{u.phone || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-[0.2em] ${u.status === "active" ? "text-white" : "text-red-400"}`}>{u.status}</span></td>
                  <td className="px-4 py-3 text-[#8E9AA6] text-xs">{u.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right space-x-3">
                    <button data-testid={`user-edit-${u.id}`} onClick={() => setEditTarget(u)} className="text-[#8E9AA6] hover:text-white" title="Edit"><Pencil size={14} /></button>
                    {!isMe && <button data-testid={`user-resetpw-${u.id}`} onClick={() => setPwTarget(u)} className="text-[#8E9AA6] hover:text-white" title="Reset password"><KeyRound size={14} /></button>}
                    {!isMe && <button data-testid={`user-delete-${u.id}`} onClick={() => setDelTarget(u)} className="text-[#8E9AA6] hover:text-red-400" title="Delete"><Trash2 size={14} /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-[#8E9AA6]">
        <div>{total} total · page {page} of {Math.max(1, Math.ceil(total / pageSize))}</div>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className={`${btn} border border-white/15 text-white disabled:opacity-30`} data-testid="admin-users-prev">Prev</button>
          <button disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} className={`${btn} border border-white/15 text-white disabled:opacity-30`} data-testid="admin-users-next">Next</button>
        </div>
      </div>

      {/* Modals */}
      <UserFormModal open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load} mode="create" />
      <UserFormModal open={!!editTarget} onClose={() => setEditTarget(null)} onSaved={load} mode="edit" target={editTarget} />
      <ResetPwModal target={pwTarget} onClose={() => setPwTarget(null)} />
      <DeleteModal target={delTarget} onClose={() => setDelTarget(null)} onDone={load} />
    </div>
  );
}

function UserFormModal({ open, mode, target, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(isEdit && target ? { ...target, password: "" } : { role: "client", status: "active", email: "", password: "", first_name: "", last_name: "", phone: "" });
  }, [isEdit, target, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (isEdit) {
        const { password, ...rest } = f;
        await api.patch(`/admin/users/${target.id}`, rest);
        toast.success("User updated.");
      } else {
        await api.post("/admin/users", f);
        toast.success("User created.");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit user" : "Add user"} testid="admin-user-form-modal">
      <form onSubmit={submit} data-testid="admin-user-form" className="space-y-5">
        <div className="grid grid-cols-2 gap-5">
          <div><label className={lbl}>First Name</label><input data-testid="form-first" required className={inp} value={f.first_name || ""} onChange={set("first_name")} /></div>
          <div><label className={lbl}>Last Name</label><input data-testid="form-last" required className={inp} value={f.last_name || ""} onChange={set("last_name")} /></div>
        </div>
        <div><label className={lbl}>Email</label><input data-testid="form-email" type="email" required className={inp} value={f.email || ""} onChange={set("email")} /></div>
        <div><label className={lbl}>Phone</label><input data-testid="form-phone" className={inp} value={f.phone || ""} onChange={set("phone")} /></div>
        <div className="grid grid-cols-2 gap-5">
          <div><label className={lbl}>Role</label>
            <select data-testid="form-role" className={inp + " bg-[#0B0B0C]"} value={f.role || "client"} onChange={set("role")}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Status</label>
            <select data-testid="form-status" className={inp + " bg-[#0B0B0C]"} value={f.status || "active"} onChange={set("status")}>
              <option value="active">active</option><option value="disabled">disabled</option>
            </select>
          </div>
        </div>
        {!isEdit && (
          <div><label className={lbl}>Initial Password</label><input data-testid="form-password" type="text" required className={inp} value={f.password || ""} onChange={set("password")} placeholder="≥ 8 chars, letter + number" /></div>
        )}
        <div className="pt-4 flex justify-end gap-3">
          <button type="button" onClick={onClose} className={`${btn} border border-white/15 text-white`}>Cancel</button>
          <button type="submit" disabled={busy} data-testid="form-submit" className={`${btn} bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50`}>{busy ? "Saving…" : isEdit ? "Save" : "Create"}</button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPwModal({ target, onClose }) {
  const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); }, [target]);
  if (!target) return null;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/admin/users/${target.id}/reset-password`, { new_password: pw }); toast.success(`Password reset for ${target.email}.`); onClose(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Reset password — ${target.email}`} testid="admin-user-resetpw-modal">
      <form onSubmit={submit} className="space-y-5">
        <div><label className={lbl}>New password</label><input data-testid="resetpw-input" type="text" required className={inp} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="≥ 8 chars, letter + number" /></div>
        <div className="pt-4 flex justify-end gap-3">
          <button type="button" onClick={onClose} className={`${btn} border border-white/15 text-white`}>Cancel</button>
          <button type="submit" disabled={busy || !pw} data-testid="resetpw-submit" className={`${btn} bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50`}>{busy ? "Resetting…" : "Reset"}</button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteModal({ target, onClose, onDone }) {
  const [typed, setTyped] = useState(""); const [busy, setBusy] = useState(false);
  const [blocking, setBlocking] = useState(null);
  useEffect(() => { setTyped(""); setBlocking(null); }, [target]);
  if (!target) return null;
  const submit = async (force) => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${target.id}`, { params: force ? { force: true } : {} });
      toast.success(`User ${target.email} deleted.`);
      onDone(); onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.blocking_bookings) {
        setBlocking(d.blocking_bookings);
      } else {
        toast.error(formatApiError(err));
      }
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Delete user" testid="admin-user-delete-modal">
      <div className="space-y-5">
        <p className="text-sm text-[#E6E6E6]">This will disable <span className="text-white">{target.email}</span>. They will no longer be able to authenticate.</p>
        <div><label className={lbl}>Type the email to confirm</label><input data-testid="delete-confirm-input" className={inp} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={target.email} /></div>
        {blocking && (
          <div className="text-xs text-red-300 border border-red-500/30 bg-red-500/5 p-3">
            User has {blocking.length} active booking(s). Force-deleting will cancel them.
            <ul className="mt-2 list-disc pl-5 text-[#E6E6E6]">{blocking.slice(0, 5).map((b) => <li key={b.id}>#{b.id.slice(0,8)} · {b.status}</li>)}</ul>
          </div>
        )}
        <div className="pt-4 flex justify-end gap-3">
          <button onClick={onClose} className={`${btn} border border-white/15 text-white`}>Cancel</button>
          {!blocking && <button data-testid="delete-submit" disabled={busy || typed !== target.email} onClick={() => submit(false)} className={`${btn} bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-30`}>{busy ? "Deleting…" : "Delete"}</button>}
          {blocking && <button data-testid="delete-force-submit" disabled={busy || typed !== target.email} onClick={() => submit(true)} className={`${btn} bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-30`}>Force delete &amp; cancel</button>}
        </div>
      </div>
    </Modal>
  );
}
